import { RefObject, useEffect, useRef, useState } from 'react';

interface Options {
  isIgnoreMouseEvents?: boolean;
  isIgnoreTouchEvents?: boolean;
}

enum EventType {
  KEYBOARD = 'keyboard',
  TOUCH = 'touch',
  MOUSE = 'mouse',
}

/**
 * useFocusTracking
 *
 * Tracks the focus state of an element and its children, returning isInFocus as true when
 * the element or any of its children is focused, and false when focus moves out.
 *
 * @param ref - a reference to the element for which focus needs to be tracked
 * @param options - an object containing options to ignore mouse or touch events
 * @returns an object containing a boolean `isInFocus` value
 */
export const useFocusTracking = (ref: RefObject<HTMLElement>, options?: Options) => {
  const [isInFocus, setIsInFocus] = useState(false);
  const lastInteractionMethodRef = useRef<'keyboard' | 'mouse' | 'touch' | null>(null);
  const { isIgnoreMouseEvents = false, isIgnoreTouchEvents = false } = options || {};

  useEffect(() => {
    const handleMouseDown = () => {
      lastInteractionMethodRef.current = EventType.MOUSE;
    };

    const handleTouchStart = () => {
      lastInteractionMethodRef.current = EventType.TOUCH;
    };

    const handleKeyDown = () => {
      lastInteractionMethodRef.current = EventType.KEYBOARD;
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const handleFocusIn = () => {
      const lastInteractionMethod = lastInteractionMethodRef.current;

      if (EventType.MOUSE === lastInteractionMethod && isIgnoreMouseEvents) {
        return;
      }

      if (EventType.TOUCH === lastInteractionMethod && isIgnoreTouchEvents) {
        return;
      }

      setIsInFocus(true);
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (element && !element.contains(event.relatedTarget as Node)) {
        setIsInFocus(false);
      }
    };

    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);

    return () => {
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
    };
  }, [ref, isIgnoreMouseEvents, isIgnoreTouchEvents]);

  return { isInFocus };
};
